"use client";

// SNOWCLUB-V101-STAGING-A1 · THE ONE ANSWER TO THE DESIGN'S `sg:pay`.
//
// Replaces `resolvePayResult` (booking-pay-bridge.ts), which answered the design's Card Details "Pay $X"
// by POSTing the legacy booking engine and — on the staging host — painting a labelled fake confirmation.
// The A1 chain is instead:
//
//   design cart (sg-cart-v1, exactly what is displayed)
//     → planDesignOrder              maps every line exactly, or refuses by name     (no network)
//     → POST /api/orders/design-cart server re-derives legs from the live board and creates a
//                                    SERVER-PRICED draft order (displayed total asserted, never charged)
//     → in-page Stripe card          InpageCardPayment, on /mountain-<code>/booking/review (authorize only)
//
// FOUNDER RULING 2026-09-26: there is no separate checkout app page. `createDesignOrder` is the shared
// order step, called by InpageCardPayment on the review page. `resolveDesignPay` — the answer for any
// `sg:pay` raised OFF that page — creates nothing and only opens the review page.
//
// THE RAIL: this function never returns `booked: true` and never an `orderId`. The design's `sg:payresult`
// handler paints CONFIRMED only on that pair, and nothing has been paid at this point — an order becomes
// paid only through the signed Stripe webhook. What comes back here is always a sentence for the design's
// notice banner.

import { designCartCheckoutHref, readDesignCart } from "@/components/marketplace/DesignCartLines";
import type { DesignCartLine } from "@/components/marketplace/DesignCartLines";
import type { CartItem } from "@/lib/cart-store";
import { currentCheckoutAttempt } from "@/lib/checkout-attempt";
import { normalizeCheckoutCurrency, readCheckoutCurrency } from "@/lib/checkout-currency";
import type { PayOrder, PayResult } from "@/lib/booking-pay-bridge";
import { createQuoteShare, createSessionContext, designQuoteKey } from "@/lib/design-cart-quote";
import { useCartStore } from "@/lib/cart-store";
import { cartNeedsCatalogue, planDesignOrder, transportRefusalCopy, transportRefusalLine } from "@/lib/design-pay-order";
import type { DesignOrderPlan, DesignOrderProduct, StoreIdentityRow } from "@/lib/design-pay-order";
import { PAY_AT_SHOP_CREATE_STATUSES, readPaymentPlan } from "@/lib/inpage-card";
import type { DepositPlan, PayAtShopPlan } from "@/lib/inpage-card";
import { sanitizeRentalTerms } from "@/lib/rental-terms";
import type { RentalGuestEntry, RentalTerms } from "@/lib/rental-terms";

export interface DesignPayDeps {
  readLines: () => DesignCartLine[];
  /** the live store feed's identity rows (window.__SG_STORE.store), re-read per plan so a reload or refetch is honoured */
  readStoreRows: () => StoreIdentityRow[];
  /**
   * Has the live store catalogue ANSWERED yet (window.__SG_STORE is set by a success, and by a failed or empty fetch alike)? Before
   * it has, a product line cannot be linked, but that is "not yet", not "no". Outside a browser nothing is awaited.
   */
  storeArrived: () => boolean;
  fetchImpl: typeof fetch;
  navigate: (url: string) => void;
  /** the path the customer is on now — never navigate to where they already are */
  currentPath: () => string;
  attemptKey: (fingerprint: string) => string;
  /** clock for quote expiry (tests inject it) */
  now: () => number;
  /** The non-secret pricing identity of the current session (see createSessionContext): customer vs guest, and which login. */
  sessionContext: () => string;
  /** The customer's checkout currency CHOICE (ISO code from the header picker). Sent as a choice; the server prices and states it. */
  currency: () => string;
}

const sessionContextOf = createSessionContext();

/**
 * What changes when WHO is priced changes — a change-detector input for createSessionContext, never output, never a cache key.
 * A bearer session is identified by its token string (as before). A cookie-backed SSO session has no token in the page, so it is
 * identified by the store's server-verified identity generation + email: guest → member, one member → another and logout → login
 * all differ, with no credential read, parsed, stored or invented. Guest (neither) is null.
 */
export function sessionIdentityOf(s: {
  session: { jwt: string | null; cookie?: boolean; email: string | null };
  identityGen?: number;
}): string | null {
  if (s.session.jwt) return s.session.jwt;
  return s.session.cookie ? `cookie:${s.identityGen ?? 0}:${s.session.email ?? ""}` : null;
}

const browserDeps = (): DesignPayDeps => ({
  sessionContext: () => sessionContextOf(sessionIdentityOf(useCartStore.getState())),
  readLines: () => readDesignCart(typeof window === "undefined" ? null : window.localStorage),
  readStoreRows: () => {
    const feed = typeof window === "undefined" ? null : (window as unknown as { __SG_STORE?: { store?: unknown } }).__SG_STORE;
    return Array.isArray(feed?.store) ? (feed.store as StoreIdentityRow[]) : [];
  },
  storeArrived: () => typeof window === "undefined" || !!(window as unknown as { __SG_STORE?: unknown }).__SG_STORE,
  fetchImpl: (...a) => fetch(...a),
  navigate: (url) => window.location.assign(url),
  currentPath: () => window.location.pathname,
  attemptKey: currentCheckoutAttempt,
  currency: readCheckoutCurrency,
  now: () => Date.now(),
});

const NOT_CHARGED = "Nothing was charged.";

/**
 * THE ONE CURRENT PLAN of the displayed cart: the design cart, the live store feed as it is RIGHT NOW (re-read on every call, so
 * a late or replaced feed is seen), and the host items. Quote, order, the off-page answer and the card panel's watcher all ask
 * this - never a second mapping - so what is priced, what is watched and what is ordered cannot diverge.
 */
export function currentDesignPlan({
  mountainCode,
  hostItems,
  order,
  deps,
}: {
  mountainCode: string;
  hostItems: CartItem[];
  order?: PayOrder;
  deps?: Partial<DesignPayDeps>;
}): DesignOrderPlan {
  const d: DesignPayDeps = { ...browserDeps(), ...(deps ?? {}) };
  const lines = d.readLines();
  // The catalogue is still on its way and this cart needs it: say so (pending), never "isn't linked". Once it has answered - even
  // with nothing, or unavailable - the plan below is the verdict and a miss is a refusal.
  if (lines.length && !d.storeArrived() && cartNeedsCatalogue(lines, hostItems)) {
    return { ok: false, pending: true, reason: "Confirming the price - the product catalogue is still loading." };
  }
  return planDesignOrder({
    mountainCode,
    lines,
    hostItems,
    tip: Number(order?.tip ?? 0) || 0,
    storePicks: order?.store ?? [],
    storeRows: d.readStoreRows(),
  });
}

/** A server-priced draft order, or the sentence that says why there is none. Nothing is charged here. */
export type DesignOrderResult =
  | {
      ok: true;
      documentId: string;
      clientTotal: number;
      held?: HeldOrderFacts;
      /** M4 · the server plan + the create's own status */
      reserved?: { plan: PayAtShopPlan; orderStatus: string };
      /** card quote · the create REPLAYED an order already past its draft: authorised only with the server's card evidence */
      cardEvidenceRequired?: true;
    }
  /** `refused`: definitely nothing was created (refused before sending, or the server answered a refusal). Absent = it may have landed. */
  | { ok: false; reason: string; refused?: true; existing?: true };

/**
 * A native allocated order the server placed a HOLD on (`orderStatus: "hold_placed"`). Every field is the server's own response,
 * unchanged: nothing is derived, defaulted or taken from the browser. A hold is not a payment and not a confirmed trip.
 */
export interface HeldOrderFacts {
  holdPlacedAt: string;
  holdExpiresAt: string;
  heldQuantity: number;
}

const validInstant = (v: unknown): v is string => typeof v === "string" && v.trim() !== "" && Number.isFinite(Date.parse(v));

/** The server's hold facts, or null when ANY required field is missing or malformed (the caller then fails closed). */
export function parseHeldOrder(json: Record<string, unknown>): HeldOrderFacts | null {
  // exactly the three hold facts the backend returns; the internal transport quote is neither required nor carried
  const { holdPlacedAt, holdExpiresAt, heldQuantity } = json;
  if (!validInstant(holdPlacedAt) || !validInstant(holdExpiresAt)) return null;
  if (typeof heldQuantity !== "number" || !Number.isSafeInteger(heldQuantity) || heldQuantity < 1) return null;
  return { holdPlacedAt, holdExpiresAt, heldQuantity };
}

/** The backend's authoritative price for the checkout lines, tied to the original cart and selected request. */
export interface DesignQuote {
  /** Only present for a partial checkout: the original cart and the selected request each retain their own fingerprint. */
  checkout?: { lineIds: string[]; excludedLineIds: string[]; fingerprint: string };
  subTotal: number;
  tax: number;
  /** Present only when the authoritative response separately states a fee. */
  fees?: number;
  total: number;
  currency: string;
  /**
   * v2 currency contract · a foreign-currency price is a frozen server quote: the opaque token is echoed unchanged on the order, the
   * expiry ends it, and the FX facts say how the server converted the CAD source price. Absent for CAD (its shape is unchanged).
   */
  quoteToken?: string;
  expiresAt?: string;
  fx?: { provider: string; cadPerUnit: string; observationDate: string | null };
  /** the CAD source total the server converted, in cents (foreign quotes only) — shown as a reference, never used to price */
  sourceTotalCents?: number;
  /** `planDesignOrder`'s fingerprint of the original cart — a selected quote never outlives that cart */
  fingerprint: string;
  /**
   * The public rental terms the server stated, by design line id — present only for lines the backend priced as a per-day rental
   * (Ski Butlers Whistler). Their guests are required on the order. Public terms only: no guest data, commission or internal note.
   */
  rentals?: Record<string, RentalTerms>;
  /**
   * M4 · present ONLY when the server's own typed plan is exactly pay-at-the-shop with nothing due now (`readPaymentPlan`). This is the
   * one thing that lets the review page skip the card, the billing address and Stripe; absent = the card path, unchanged.
   */
  payment?: PayAtShopPlan;
  /**
   * M4-D · present ONLY when the server's typed plan is exactly the deposit plan (FE contract Q1). The card rail is unchanged: `total` IS
   * the deposit due now, so the asserted total and the PaymentIntent amount are the deposit. The balance and the full pre-tax price are
   * the server's, shown as terms, never charged or derived here.
   */
  deposit?: DepositPlan;
}
export type DesignQuoteResult = ({ ok: true } & DesignQuote) | { ok: false; reason: string; pending?: true; excludedLineIds?: string[] };

const toCents = (n: number) => Math.round(n * 100);
const firstName = (l: DesignCartLine) => [l.name, l.vendor].find((v) => typeof v === "string" && v.trim() !== "")?.trim() ?? "";

/**
 * THE READ-ONLY PRICE QUESTION, ASKED BEFORE THE CARD. Plans the displayed cart exactly (or refuses by name),
 * then asks `/api/orders/design-cart` (`quoteOnly`) for snowclub-be's `customer-cart-quote`. No order, no
 * payment, no idempotency key. FAILS CLOSED: any refusal, unreachable backend or incomplete figure is a
 * reason, never a number — the browser derives no price of its own (the displayed CAD 33 against the
 * backend's CAD 34.65 is exactly the defect this replaces).
 */
export async function quoteDesignOrder({
  mountainCode,
  hostItems,
  order,
  deps,
}: {
  mountainCode: string;
  hostItems: CartItem[];
  order?: PayOrder;
  deps?: Partial<DesignPayDeps>;
}): Promise<DesignQuoteResult> {
  const d: DesignPayDeps = { ...browserDeps(), ...(deps ?? {}) };
  const excludedLineIds: string[] = [];
  const fullPlan = currentDesignPlan({ mountainCode, hostItems, order, deps: d });
  if (!fullPlan.ok) return fullPlan.pending ? { ok: false, reason: fullPlan.reason, pending: true } : { ok: false, reason: fullPlan.reason };
  const originalLines = d.readLines().map((l) => ({ ...l }));
  let selected = originalLines;
  let plan = fullPlan;
  for (;;) {
    try {
      const { mountainCode: mc, transport, products } = plan.request;
      const currency = normalizeCheckoutCurrency(d.currency());
      const res = await d.fetchImpl("/api/orders/design-cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mountainCode: mc, transport, products, currency, quoteOnly: true }),
      });
      const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok && json.refusal === "rental_dates_required") {
        // the server needs a start AND end date for a per-day rental: name the lines missing them and point at their date controls
        // only non-blank display names are kept, so a missing OR blank name falls back the same way through `??`
        const names = new Map(d.readLines().flatMap((l): [string, string][] => (firstName(l) ? [[l.id, firstName(l)]] : [])));
        const missing = plan.request.products.filter((p) => !p.useStartDate || !p.useEndDate).map((p) => `"${names.get(p.lineId) ?? "your rental"}"`);
        return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `Choose the start and end dates for ${missing.length ? missing.join(", ") : "your rental"} in your cart — rentals are priced per day. ${NOT_CHARGED}` };
      }
      if (!res.ok) {
        const departure = transportRefusalCopy(res.status, json.refusal);
        if (departure) {
          const current = currentDesignPlan({ mountainCode, hostItems, order, deps: d });
          const line = current.ok && current.fingerprint === fullPlan.fingerprint && JSON.stringify(current.request) === JSON.stringify(fullPlan.request)
            ? transportRefusalLine(plan.request, json.itemIndex) : null;
          // Keep the refused item visible and refuse the WHOLE quote. No partial transport retry or deletion.
          return { ok: false, reason: `${departure} ${NOT_CHARGED}`, ...(excludedLineIds.length || line ? { excludedLineIds: [...excludedLineIds, ...(line ? [line.lineId] : [])] } : {}) };
        }
        const reason = typeof json.error === "string" ? json.error : `The price could not be confirmed (${res.status}). ${NOT_CHARGED}`;
        // The banked production BE names a specific product on these refusals. No network, tax,
        // mode, authentication or ambiguous failure may silently turn into a smaller checkout.
        const changed = res.status === 400 ? /^(?:Unknown product: ([a-zA-Z0-9_-]+)|Product ([a-zA-Z0-9_-]+) (?:is not available(?: on (\d{4}-\d{2}-\d{2}))?|carries no server price))\. Nothing was charged\.$/.exec(reason) : null;
        const productId = changed?.[1] ?? changed?.[2];
        const refusedDate = changed?.[3];
        const refused = productId ? plan.request.products.filter((p) => p.productDocumentId === productId && (!refusedDate || p.useStartDate === refusedDate)).map((p) => p.lineId) : [];
        if (!refused.length) return { ok: false, reason, ...(excludedLineIds.length ? { excludedLineIds } : {}) };
        excludedLineIds.push(...refused);
        selected = selected.filter((l) => !refused.includes(l.id));
        if (!selected.length) return { ok: false, reason: "This item has changed - update or remove it", excludedLineIds };
        const selectedOrder = order ? { ...order, store: order.store?.filter((p) => !excludedLineIds.includes(`store-${String(p.id)}`)) } : order;
        const next = currentDesignPlan({ mountainCode, hostItems, order: selectedOrder, deps: { ...d, readLines: () => selected } });
        if (!next.ok) return { ok: false, reason: next.reason, excludedLineIds };
        plan = next;
        continue; // each retry removes at least one explicitly refused line; never a per-line total sum
      }
      const { subTotal, tax, total } = json;
      const finite = (n: unknown): n is number => typeof n === "number" && Number.isFinite(n) && n >= 0;
      const rawFees = json.fees;
      const hasFees = rawFees !== undefined;
      if (hasFees && !finite(rawFees)) return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `The price could not be confirmed. ${NOT_CHARGED}` };
      const fees = hasFees ? rawFees : undefined;
      const expectedTotal = toCents(subTotal as number) + toCents(tax as number) + (fees === undefined ? 0 : toCents(fees));
      if (!finite(subTotal) || !finite(tax) || !finite(total) || total <= 0 || typeof json.currency !== "string" || !json.currency || expectedTotal !== toCents(total)) {
        return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `The price could not be confirmed. ${NOT_CHARGED}` };
      }
      // The price is in the currency the customer chose, or it is not their price: a quote in another currency is refused, never relabelled.
      if (json.currency !== currency) {
        return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `The price could not be confirmed in ${currency}. Choose another currency in the header, or try again. ${NOT_CHARGED}` };
      }
      // v2 · a foreign price must carry the server's own quote token, expiry and FX facts (the BFF already checked their shape)
      const quoteToken = typeof json.quoteToken === "string" && json.quoteToken ? json.quoteToken : undefined;
      const expiresAt = typeof json.expiresAt === "string" && Number.isFinite(Date.parse(json.expiresAt)) ? json.expiresAt : undefined;
      const fxRaw = json.fx as DesignQuote["fx"] | undefined;
      const fx = fxRaw && typeof fxRaw.provider === "string" && typeof fxRaw.cadPerUnit === "string" ? { provider: fxRaw.provider, cadPerUnit: fxRaw.cadPerUnit, observationDate: typeof fxRaw.observationDate === "string" ? fxRaw.observationDate : null } : undefined;
      if (currency !== "CAD" && (!quoteToken || !expiresAt || !fx)) {
        return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `The price could not be confirmed in ${currency}. ${NOT_CHARGED}` };
      }
      const sourceTotalCents = typeof json.sourceTotalCents === "number" && Number.isSafeInteger(json.sourceTotalCents) && json.sourceTotalCents > 0 ? json.sourceTotalCents : undefined;
      const rentals = provenRentals(json.rentals, plan.request.products);
      if ("reason" in rentals) return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: rentals.reason };
      // How this booking is paid is the server's word too: exactly the card plan, exactly the M4 zero-due plan, or a refusal.
      const payment = readPaymentPlan(json.payment, total, json.currency);
      if (payment.kind === "invalid") return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `We couldn't confirm the payment details. ${NOT_CHARGED}` };
      return {
        ok: true,
        subTotal,
        tax,
        ...(fees === undefined ? {} : { fees }),
        total,
        currency: json.currency,
        ...(quoteToken ? { quoteToken } : {}),
        ...(expiresAt ? { expiresAt } : {}),
        ...(fx ? { fx } : {}),
        ...(sourceTotalCents ? { sourceTotalCents } : {}),
        fingerprint: fullPlan.fingerprint,
        ...(excludedLineIds.length ? { checkout: { lineIds: selected.map((l) => l.id), excludedLineIds, fingerprint: plan.fingerprint } } : {}),
        ...(Object.keys(rentals.terms).length ? { rentals: rentals.terms } : {}),
        ...(payment.kind === "pay_at_shop" ? { payment: payment.plan } : {}),
        ...(payment.kind === "deposit" ? { deposit: payment.plan } : {}),
      };
    } catch (e) {
      return { ok: false, ...(excludedLineIds.length ? { excludedLineIds } : {}), reason: `The price could not be confirmed (${e instanceof Error ? e.message : "network"}). ${NOT_CHARGED}` };
    }
  }
}

/**
 * AFTER THE AUTHORITATIVE ANSWER: the rental terms the server stated, re-read through the allowlist and tied to the planned product
 * with that exact line id and dates. A multi-day line the server did NOT price as a per-day rental for exactly its displayed days is
 * refused here, as before - per-unit products are never turned into per-day hires by the browser.
 */
function provenRentals(raw: unknown, products: DesignOrderProduct[]): { terms: Record<string, RentalTerms> } | { reason: string } {
  const unreadable = { reason: `The rental terms could not be confirmed. ${NOT_CHARGED}` };
  const terms: Record<string, RentalTerms> = {};
  if (raw !== undefined) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return unreadable;
    for (const [lineId, value] of Object.entries(raw as Record<string, unknown>)) {
      const p = products.find((x) => x.lineId === lineId);
      const t = sanitizeRentalTerms(value);
      if (!p || !t || t.startDate !== p.useStartDate || t.endDate !== p.useEndDate || t.guestsRequired !== p.quantity) return unreadable;
      terms[lineId] = t;
    }
  }
  for (const p of products) {
    if ((p.days ?? 1) === 1) continue;
    if (terms[p.lineId]?.days !== p.days) {
      return { reason: `This item is priced per day, and card payment can only take single-day items for it today. Remove it or change it to one day to continue. ${NOT_CHARGED}` };
    }
  }
  return { terms };
}

// ── THE SHARED SERVER QUOTE (design-cart-quote.ts holds the keying/dedup; the price is still quoteDesignOrder's) ─────────────
const quoteShare = createQuoteShare<DesignQuoteResult>({ ttlMs: 15_000 });
interface SharedQuoteArgs {
  mountainCode: string;
  hostItems: CartItem[];
  order?: PayOrder;
  deps?: Partial<DesignPayDeps>;
  now?: () => number;
}

/** The pricing identity of the session RIGHT NOW (injected in tests, the app's own session in production). Every current-check compares it. */
export function currentDesignQuoteContext(deps?: Partial<DesignPayDeps>): string {
  const d = { ...browserDeps(), ...(deps ?? {}) } as DesignPayDeps;
  // the chosen currency is part of the question too: the same cart priced in CAD and in USD is two answers, never one relabelled
  const currency = normalizeCheckoutCurrency(d.currency());
  return currency === "CAD" ? d.sessionContext() : `${d.sessionContext()}§cur:${currency}`;
}

/** The key of the question the CURRENT plan would put to the server, or null when the cart is not orderable (nothing to key). The session
 * context is part of it: the server prices customer vs guest from the session, so one cart under two sessions is two questions. */
function sharedQuoteKey({ mountainCode, hostItems, order, deps }: SharedQuoteArgs): { key: string; fingerprint: string; context: string } | null {
  const plan = currentDesignPlan({ mountainCode, hostItems, order, deps });
  if (!plan.ok) return null;
  const context = currentDesignQuoteContext(deps); // session + chosen currency
  const { mountainCode: mc, transport, products } = plan.request; // exactly the POST body's content; clientTotal is a client estimate and is not part of it
  return { key: designQuoteKey({ request: { mountainCode: mc, transport, products }, fingerprint: plan.fingerprint, scope: context }), fingerprint: plan.fingerprint, context };
}

/** A fresh, already-validated server quote for EXACTLY the cart as planned now, or null. Lets a remount/navigation show the answer at once
 * instead of a pending state it does not need. Never an amount for a different cart: a changed input is a different key. */
export function peekSharedDesignQuote(args: SharedQuoteArgs): (DesignQuoteResult & { ok: true }) | null {
  const k = sharedQuoteKey(args);
  if (!k) return null;
  const hit = quoteShare.peek(k.key, (args.now ?? Date.now)());
  return hit && hit.ok && hit.fingerprint === k.fingerprint && !isRejectedDesignQuote(hit) ? hit : null;
}

/** `quoteDesignOrder`, shared: the same cart asked by several consumers or re-asked by a remount is ONE request, and a settled success is
 * reused briefly. Anything not orderable, pending or failed goes straight through and is never remembered. */
export async function sharedDesignQuote(args: SharedQuoteArgs): Promise<DesignQuoteResult> {
  const k = sharedQuoteKey(args);
  const ask = () => quoteDesignOrder({ mountainCode: args.mountainCode, hostItems: args.hostItems, order: args.order, deps: args.deps });
  if (!k) return ask();
  return quoteShare.run(k.key, ask, {
    now: args.now,
    // remembered only if the answer is a success priced for the very plan the key was made from (the plan is re-read inside the ask)
    // ...and only if the session that asked is still the session (a login/logout while it was in flight makes it nobody's to reuse).
    // ...and never a quote the server has since rejected (an older in-flight answer carrying a dead token).
    accept: (r) => r.ok && r.fingerprint === k.fingerprint && currentDesignQuoteContext(args.deps) === k.context && !isRejectedDesignQuote(r),
  });
}

/** Test seam: forget every shared quote. */
export function resetSharedDesignQuote(): void {
  quoteShare.clear();
  rejectedQuoteTokens.clear();
}

// ── A QUOTE THE SERVER REJECTED (PR #458 review F1) ─────────────────────────────────────────────────────────────────────────────
// `QUOTE_INVALID_OR_CHANGED` is the backend's authoritative "this signed price is no longer the price" — even when the cart, the
// session and the currency are unchanged and the quote has not expired (the fare or terms moved server-side). That exact quote is
// dead: it is dropped from the shared cache, never shown or reused again, and every consumer asks the backend for its replacement
// before Pay can create anything. Rejections are remembered by object (any currency, CAD included) and by the server's opaque token
// (so an older in-flight answer carrying it is refused too). The token is only compared, never sent anywhere or logged.
const rejectedQuotes = new WeakSet<object>();
const rejectedQuoteTokens = new Set<string>();
/** The server refused this quote: it must never price or create anything again. */
export function isRejectedDesignQuote(q: unknown): boolean {
  if (!q || typeof q !== "object") return false;
  const token = (q as { quoteToken?: unknown }).quoteToken;
  return rejectedQuotes.has(q) || (typeof token === "string" && rejectedQuoteTokens.has(token));
}
/** Mark a quote rejected, drop every cached quote, and tell both quote consumers (summary and card panel) to ask again now. */
export function invalidateDesignQuote(q: DesignQuote): void {
  rejectedQuotes.add(q);
  if (q.quoteToken) rejectedQuoteTokens.add(q.quoteToken);
  quoteShare.clear();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") window.dispatchEvent(new Event("sg:cartquotechange"));
}

/**
 * THE ONE ORDER-CREATION STEP both card surfaces share: the in-page CARD DETAILS on the review URL
 * (`InpageCardPayment`, founder correction 2026-09-25). The design cart is
 * planned exactly (or refused by name), then POSTed to `/api/orders/design-cart`, which re-derives every
 * leg from the live board and asks the backend for a SERVER-PRICED draft. The asserted total is the
 * BACKEND'S quote the customer was shown (`quoteDesignOrder`) — never a figure derived here — and the
 * backend still refuses on mismatch. No quote for this exact cart is refused before any request: no draft,
 * no payment. The attempt key is NOT ended here: the caller ends it once the order is safely handed to
 * payment, so a retry of a failed attempt gets the same order back.
 */
export async function createDesignOrder({
  mountainCode,
  hostItems,
  order,
  quote,
  guests,
  deps,
}: {
  mountainCode: string;
  hostItems: CartItem[];
  order: PayOrder;
  /** the quote the customer was shown; null or for a different cart = refuse */
  quote: DesignQuote | null;
  /** page-memory guests per rented line (`buildRentalGuests`); sent ONLY on this order request, only for lines the quote named */
  guests?: Record<string, RentalGuestEntry[]> | null;
  deps?: Partial<DesignPayDeps>;
}): Promise<DesignOrderResult> {
  const d: DesignPayDeps = { ...browserDeps(), ...(deps ?? {}) };

  let plan = currentDesignPlan({ mountainCode, hostItems, order, deps: d });
  if (!plan.ok) return { ok: false, refused: true, reason: plan.reason };
  if (!quote) return { ok: false, refused: true, reason: `The price hasn't been confirmed yet. ${NOT_CHARGED}` };
  // F1 · a quote the server already rejected is never sent again: its replacement is being fetched
  if (isRejectedDesignQuote(quote)) return { ok: false, refused: true, reason: `Your price is being refreshed. Check the new total, then press Pay again. ${NOT_CHARGED}` };
  if (quote.fingerprint !== plan.fingerprint) {
    return { ok: false, refused: true, reason: `Your cart changed after the price was shown — the price is being refreshed. ${NOT_CHARGED}` };
  }
  if (quote.checkout) {
    const all = d.readLines();
    const { lineIds, excludedLineIds, fingerprint } = quote.checkout;
    const chosen = new Set(lineIds), excluded = new Set(excludedLineIds);
    if (!chosen.size || chosen.size !== lineIds.length || excluded.size !== excludedLineIds.length ||
        all.length !== chosen.size + excluded.size || all.some((l) => chosen.has(l.id) === excluded.has(l.id))) {
      return { ok: false, refused: true, reason: `The checkout items changed. Confirm the price again. ${NOT_CHARGED}` };
    }
    const selected = all.filter((l) => chosen.has(l.id));
    const selectedOrder = order ? { ...order, store: order.store?.filter((p) => !excluded.has(`store-${String(p.id)}`)) } : order;
    plan = currentDesignPlan({ mountainCode, hostItems, order: selectedOrder, deps: { ...d, readLines: () => selected } });
    if (!plan.ok || plan.fingerprint !== fingerprint) return { ok: false, refused: true, reason: `The checkout items changed. Confirm the price again. ${NOT_CHARGED}` };
  }
  if (quote.currency !== normalizeCheckoutCurrency(d.currency())) {
    return { ok: false, refused: true, reason: `The currency changed after the price was shown — the price is being refreshed. ${NOT_CHARGED}` };
  }
  // v2 · an expired server quote is never sent: the backend would refuse it, so the price is re-asked first and nothing is created
  if (quote.expiresAt && Date.parse(quote.expiresAt) <= d.now()) {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") window.dispatchEvent(new Event("sg:cartquotechange"));
    return { ok: false, refused: true, reason: `Your price in ${quote.currency} expired and is being refreshed. Check the new total, then press Pay again. ${NOT_CHARGED}` };
  }
  const rented = quote.rentals ?? {};
  if (Object.keys(rented).some((lineId) => !guests?.[lineId]?.length)) {
    return { ok: false, refused: true, reason: `Add the rental guest details above to continue. ${NOT_CHARGED}` };
  }
  const products = plan.request.products.map((p) => {
    const lineGuests = rented[p.lineId] ? guests?.[p.lineId] : undefined;
    return lineGuests ? { ...p, guests: lineGuests } : p;
  });

  try {
    const res = await d.fetchImpl("/api/orders/design-cart", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // one attempt per cart AND currency: the same idempotency key in another currency is a backend conflict, so a new currency is a new attempt
      body: JSON.stringify({
        ...plan.request,
        products,
        currency: quote.currency,
        ...(quote.quoteToken ? { quoteToken: quote.quoteToken } : {}),
        clientTotal: quote.total,
        idempotencyKey: d.attemptKey(quote.currency === "CAD" ? plan.fingerprint : `${plan.fingerprint}§cur:${quote.currency}`),
      }),
    });
    const json = (await res.json().catch(() => ({}))) as { documentId?: unknown; error?: unknown; orderStatus?: unknown } & Record<string, unknown>;
    if (res.status === 409 && json.code === "ORDER_CURRENCY_CONFLICT") {
      return { ok: false, existing: true, reason: typeof json.error === "string" ? json.error : `This booking was already started in another currency. Do not pay again — check My Trips.` };
    }
    // the server said this signed price is no longer the price: kill it (cache + token) and make both consumers fetch its replacement
    if (json.code === "QUOTE_INVALID_OR_CHANGED") invalidateDesignQuote(quote);
    if (res.status === 409 && json.code === "rental_replay_conflict") {
      // an order already exists under this attempt with other rental details: an EXISTING booking, never "nothing was created"
      return { ok: false, existing: true, reason: typeof json.error === "string" ? json.error : `This booking was already started. Do not pay again — check My Trips. ${NOT_CHARGED}` };
    }
    if (!res.ok) {
      const departure = transportRefusalCopy(res.status, json.refusal);
      if (departure) {
        const line = transportRefusalLine(plan.request, json.itemIndex);
        const name = line ? `The submitted ${line.vendor} departure on ${line.date} at ${line.dep} from ${line.depStop}: ` : "";
        return { ok: false, refused: true, reason: `${name}${departure} ${NOT_CHARGED}` };
      }
      return { ok: false, refused: true, reason: typeof json.error === "string" ? json.error : `Checkout refused (${res.status}). ${NOT_CHARGED}` };
    }
    const documentId = typeof json.documentId === "string" ? json.documentId : "";
    if (!documentId) return { ok: false, reason: `Checkout did not return an order. ${NOT_CHARGED}` };
    // v2 · the order's frozen currency (absent = CAD) and total must be the quote's: an order in another currency or for another amount
    // EXISTS, so it is locked (never a card for it, never relabelled)
    const orderCurrency = typeof json.currency === "string" ? json.currency : "CAD";
    const frozenTotalOk = orderCurrency === "CAD" || Boolean(quote.payment) || (typeof json.total === "number" && toCents(json.total) === toCents(quote.total));
    if (orderCurrency !== quote.currency || !frozenTotalOk) {
      return { ok: false, existing: true, reason: `This booking was created in ${orderCurrency}, not the ${quote.currency} price shown, so no card is asked for. Do not book again — check My Trips. ${NOT_CHARGED}` };
    }
    if (quote.payment) {
      // M4 · the quote said pay at the shop: the create must say so too — an M4 status for the quote's exact total. A draft, a hold,
      // `paid` or a different total means the order's mode is not what was shown: it EXISTS, so it is locked (never a card for it).
      const status = typeof json.orderStatus === "string" ? json.orderStatus : "";
      const sameTotal = typeof json.total === "number" && Number.isFinite(json.total) && toCents(json.total) === quote.payment.balanceDueCents;
      if (!(PAY_AT_SHOP_CREATE_STATUSES as readonly string[]).includes(status) || !sameTotal) {
        return { ok: false, existing: true, reason: `This booking could not be confirmed as a pay-at-the-shop reservation, so no card is asked for. ${NOT_CHARGED} Do not book again — check My Trips.` };
      }
      return { ok: true, documentId, clientTotal: quote.total, reserved: { plan: quote.payment, orderStatus: status } };
    }
    // Card quote · the create's own facts are reconciled before the card mechanism: a FRESH card order is a draft (or a hold). A fresh
    // `pending_acceptance` / `cancelled` is an M4 reservation the server made although the quote shown was a card one (the product's
    // mode changed after quoting): it EXISTS, so it is locked — never a card, never "authorised", never a second order. A replay already
    // past its draft may be this card flow's own earlier order, so it goes on only on condition of the server's card evidence.
    const createStatus = typeof json.orderStatus === "string" ? json.orderStatus : "";
    const replayed = json.replayed === true;
    if (!replayed && (createStatus === "pending_acceptance" || createStatus === "cancelled")) {
      return { ok: false, existing: true, reason: `This booking was placed as a pay-at-the-shop reservation, not the card payment shown, so no card is asked for. ${NOT_CHARGED} Do not book again — check My Trips.` };
    }
    if (json.orderStatus === "hold_placed") {
      // A hold is read ONLY from the server's own response; a missing or malformed hold fact is a refusal (a backend contract gap), never a guess.
      const held = parseHeldOrder(json);
      if (!held) return { ok: false, reason: `Your seats could not be held: the reservation details were incomplete. ${NOT_CHARGED}` };
      return { ok: true, documentId, clientTotal: quote.total, held };
    }
    if (replayed && createStatus !== "" && createStatus !== "draft") return { ok: true, documentId, clientTotal: quote.total, cardEvidenceRequired: true };
    return { ok: true, documentId, clientTotal: quote.total };
  } catch (e) {
    return {
      ok: false,
      reason: `Checkout could not be reached (${e instanceof Error ? e.message : "network"}). ${NOT_CHARGED}`,
    };
  }
}

export type DesignQuoteWatchState = { status: "loading" } | { status: "ok"; quote: DesignQuote } | { status: "failed"; reason: string };

/**
 * WATCH THE DISPLAYED CART'S PRICE. Every tick asks the one current plan (`currentDesignPlan`) what is being bought, and a changed
 * answer - a quantity or date edit, or the live store feed arriving late or being replaced (which changes a product's resolved
 * native id, and so the fingerprint) - is a new question for the server quote. A refusal is also an answer, keyed by its reason,
 * so it re-asks the moment the plan becomes orderable, with no manual retry. An answer for a plan that is no longer current, or
 * that arrives after `stop()`, is dropped. Money comes only from `quoteDesignOrder`; nothing here derives an amount.
 */
export function startDesignQuoteWatch({
  mountainCode,
  getHostItems,
  onState,
  deps,
  everyMs = 1000,
  schedule = (fn: () => void, ms: number) => {
    const t = setInterval(fn, ms);
    return () => clearInterval(t);
  },
}: {
  mountainCode: string;
  getHostItems: () => CartItem[];
  onState: (s: DesignQuoteWatchState) => void;
  deps?: Partial<DesignPayDeps>;
  everyMs?: number;
  schedule?: (fn: () => void, ms: number) => () => void;
}): () => void {
  let live = true;
  let seen: string | null = null;
  // v2 · a foreign server quote expires: once it has, the same question is asked again (its token can no longer create an order)
  let expiresAt: number | null = null;
  /** the quote this watcher last showed: if the server rejects it, the unchanged question is asked again (F1) */
  let shown: DesignQuote | null = null;
  const clock = () => ({ ...browserDeps(), ...(deps ?? {}) } as DesignPayDeps).now();
  const keyOf = () => {
    const p = currentDesignPlan({ mountainCode, hostItems: getHostItems(), deps });
    // the session context is part of the question: a login/logout/customer change re-asks even for an identical cart
    return `${p.ok ? p.fingerprint : p.pending ? "pending:catalogue" : `refused:${p.reason}`}§${currentDesignQuoteContext(deps)}`;
  };
  const run = () => {
    if (!live) return;
    const k = keyOf();
    if (k === seen && !(expiresAt !== null && expiresAt <= clock()) && !(shown && isRejectedDesignQuote(shown))) return;
    seen = k;
    expiresAt = null;
    shown = null;
    // A validated quote for exactly this plan already exists (another surface or an earlier mount asked it): show it, no pending flash
    // and no second request. Otherwise this is a genuine wait, and the request is the shared one.
    const known = peekSharedDesignQuote({ mountainCode, hostItems: getHostItems(), deps });
    if (known && `${known.fingerprint}§${currentDesignQuoteContext(deps)}` === k && !(known.expiresAt && Date.parse(known.expiresAt) <= clock())) {
      expiresAt = known.expiresAt ? Date.parse(known.expiresAt) : null;
      shown = known;
      onState({ status: "ok", quote: known });
      return;
    }
    onState({ status: "loading" });
    void sharedDesignQuote({ mountainCode, hostItems: getHostItems(), deps }).then((q) => {
      if (!live) return;
      if (!q.ok && q.pending) return; // still waiting on the catalogue: stay loading; the tick that sees it land asks again
      // Completion is checked against the plan as it is NOW (not against the last tick's key): a cart, date or feed change that
      // landed while this was in flight makes the answer stale even if no tick has run yet. Discard it and ask the current question now.
      if (seen !== k || keyOf() !== k || (q.ok && `${q.fingerprint}§${currentDesignQuoteContext(deps)}` !== k)) {
        if (seen === k) seen = null; // nothing newer has started: force the re-ask
        run();
        return;
      }
      if (q.ok && isRejectedDesignQuote(q)) {
        // an answer carrying a token the server already rejected (it was in flight): never shown; the next tick asks again
        seen = null;
        onState({ status: "loading" });
        return;
      }
      if (q.ok && q.expiresAt) expiresAt = Date.parse(q.expiresAt);
      if (q.ok) shown = q;
      onState(q.ok ? { status: "ok", quote: q } : { status: "failed", reason: q.reason });
    });
  };
  run();
  const cancel = schedule(run, everyMs);
  // the design raises this on every cart edit and when the catalogue lands: re-plan at once instead of waiting for the next tick
  const onChange = () => run();
  if (typeof window !== "undefined") window.addEventListener("sg:cartquotechange", onChange);
  return () => {
    live = false;
    cancel();
    if (typeof window !== "undefined") window.removeEventListener("sg:cartquotechange", onChange);
  };
}

/**
 * The answer to an `sg:pay` raised OFF the review page (that page's own answerer is InpageCardPayment).
 * A cart that cannot be ordered still says why, by name; one that can is sent to the canonical review
 * page, where the card is taken and the order is created. No order is created, and no request is made,
 * here — and never a navigation to the page the customer is already on.
 */
export function resolveDesignPay({
  mountainCode,
  hostItems,
  order,
  deps,
}: {
  mountainCode: string;
  hostItems: CartItem[];
  order: PayOrder;
  deps?: Partial<DesignPayDeps>;
}): PayResult {
  const d: DesignPayDeps = { ...browserDeps(), ...(deps ?? {}) };
  const plan = currentDesignPlan({ mountainCode, hostItems, order, deps: d });
  if (!plan.ok) return { booked: false, reason: plan.reason };

  const review = designCartCheckoutHref(mountainCode);
  if (d.currentPath() === review) {
    return { booked: false, reason: `Secure card entry is still loading — try again in a moment. ${NOT_CHARGED}` };
  }
  d.navigate(review);
  return { booked: false, reason: `Opening card details on the booking page. ${NOT_CHARGED}` };
}
